/** biome-ignore-all lint/a11y/useAriaPropsForRole: Separator with label uses role="separator" which is valid without aria-valuenow for non-focusable separators per WAI-ARIA spec */
/** biome-ignore-all lint/a11y/useSemanticElements: Custom separator with label requires div for flex layout to center text between lines */
/** biome-ignore-all lint/a11y/useFocusableInteractive: Non-focusable separator per WAI-ARIA spec for decorative separators */

'use client';

import { Separator as SeparatorPrimitive } from '@base-ui/react/separator';
import * as React from 'react';

import { cn } from '@/lib/utils';

type SeparatorAppearance = 'single' | 'dual' | 'filled';
type SeparatorLabelVariant = 'text' | 'heading';

interface SeparatorProps extends Omit<SeparatorPrimitive.Props, 'children'> {
  /**
   * Visual treatment from the Humain Design Library.
   * `single` keeps the existing one-line separator behavior.
   */
  appearance?: SeparatorAppearance;
  label?: string;
  /** Typography treatment for the optional centered label. */
  labelVariant?: SeparatorLabelVariant;
  /** Optional centered content, such as a Button or ToggleGroup. */
  children?: React.ReactNode;
}

const separatorLineClassName = 'h-px min-w-px flex-1 bg-border';

const separatorLabelClassNames: Record<SeparatorLabelVariant, string> = {
  text: 'text-secondary-foreground text-sm font-medium leading-5',
  heading: 'text-foreground text-lg font-semibold leading-7',
};

/**
 * @llms Visual separator supporting single-line, dual-line, and background-fill Figma treatments with optional text, heading, or custom center content.
 */
const Separator = React.forwardRef<HTMLDivElement, SeparatorProps>(
  (
    {
      className,
      orientation = 'horizontal',
      appearance = 'single',
      label,
      labelVariant = 'text',
      children,
      style,
      ...props
    },
    ref,
  ) => {
    const normalizedLabel = label?.trim();
    const hasCustomContent =
      children !== undefined && children !== null && children !== false;
    const hasLabelContent = !hasCustomContent && Boolean(normalizedLabel);
    const hasHorizontalContent =
      orientation === 'horizontal' && (hasCustomContent || hasLabelContent);
    const separatorAriaProps = hasLabelContent
      ? {
          role: 'separator',
          'aria-orientation': orientation,
          'aria-label': normalizedLabel,
        }
      : {};

    if (hasHorizontalContent) {
      const content = hasLabelContent ? (
        <span
          aria-hidden="true"
          className={cn(
            'shrink-0 whitespace-nowrap text-center',
            separatorLabelClassNames[labelVariant],
          )}
        >
          {normalizedLabel}
        </span>
      ) : (
        children
      );

      return (
        <div
          ref={ref}
          {...separatorAriaProps}
          data-slot="separator"
          data-orientation={orientation}
          data-appearance={appearance}
          data-label-variant={hasLabelContent ? labelVariant : undefined}
          className={cn(
            'w-full shrink-0',
            appearance === 'single' && 'flex items-center justify-center gap-2',
            appearance === 'dual' &&
              'flex items-center justify-center border-y border-border py-3',
            appearance === 'filled' &&
              'flex items-center justify-center rounded-md bg-muted py-2',
            className,
          )}
          style={typeof style === 'function' ? undefined : style}
          {...props}
        >
          {appearance === 'single' && (
            <span
              data-slot="separator-line"
              aria-hidden="true"
              className={separatorLineClassName}
            />
          )}
          <div data-slot="separator-content" className="shrink-0">
            {content}
          </div>
          {appearance === 'single' && (
            <span
              data-slot="separator-line"
              aria-hidden="true"
              className={separatorLineClassName}
            />
          )}
        </div>
      );
    }

    return (
      <SeparatorPrimitive
        ref={ref}
        data-slot="separator"
        orientation={orientation}
        style={style}
        className={cn(
          'bg-border shrink-0 data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-4 data-[orientation=vertical]:w-px',
          className,
        )}
        {...props}
      />
    );
  },
);

Separator.displayName = 'Separator';

export type { SeparatorAppearance, SeparatorLabelVariant, SeparatorProps };
export { Separator };
